import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import ToolTip from "components/design/toolTip";
import Card from "components/DesignSystem/Card/Card";
import { DottedRoundCheck } from "components/icons/DottedRoundCheck";
import { Redirect } from "components/icons/Redirect";
import { SolidRoundCheck } from "components/icons/SolidRoundCheck";
import { InfoSolid } from "components/InfoSolid";
import {
  CHECKLIST_ITEM_SECTION,
  CHECKLIST_ITEM_TYPE,
} from "constants/financialClosing";
import {
  FINANCIAL_CLOSING_CHECKLIST_LABEL,
  FINANCIAL_CLOSING_CHECKLIST_TOOLTIP_TEXT,
  REVIEW_CHANGES_LINK,
} from "dictionaries";
import { useFormikContext } from "formik";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import React, { ButtonHTMLAttributes, ComponentProps } from "react";
import { useLocation } from "react-router-dom";
import {
  ChecklistItem,
  FinancialClosingDetail,
  FinancialClosing,
  useUpdateFinancialClosingCheckListMutation,
} from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { CheckListAccordion } from "./CheckListAccordion";
import FinalizeFinancials from "./FinalizeFinancials";
import { RnDBifurcationSection } from "./RnDBifurcationSection";
import { VerticalDivider } from "components/design/VerticalDivider";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Circle } from "components/icons/Circle";
import { CLOSING_STATUS } from "constants/financialClosing";
import { OtherAttachemetsView } from "./OtherAttachemetsView";
import { CircleRed } from "components/icons/CircleRed";
import { formatNumber } from "utils/formatNumber";

export const CategorisedIcon = {
  SYSTEM_SUGGESTED: DottedRoundCheck,
  MANUAL_ACCEPTED: SolidRoundCheck,
  NOT_ACCEPTED: Circle,
};

export type CheckListUpdatePayload = {
  checklistItemId: string;
  resolved_by_admin?: boolean;
  file_url?: string;
  file_data?: File;
  document_id?: string;
  document_type?: string;
  delete_document?: boolean;
};

export const getStatus = ({
  resolved_by_system,
  resolved_by_admin,
}: {
  resolved_by_system: boolean;
  resolved_by_admin: boolean;
}) => {
  if (resolved_by_admin) {
    return "MANUAL_ACCEPTED";
  } else if (resolved_by_system) {
    return "SYSTEM_SUGGESTED";
  } else {
    return "NOT_ACCEPTED";
  }
};

export const Info = (
  props: Omit<ComponentProps<typeof ToolTip>, "children">
) => {
  return (
    <ToolTip {...props}>
      <span className="t-text-text-30 t-flex t-items-center t-justify-center">
        <InfoSolid size="16" color="currentColor" />
      </span>
    </ToolTip>
  );
};

export const ActionItem = ({
  label,
  description,
  status,
  onIconClick,
  parentType,
  isInReview,
  showCompleted,
  showError,
  id,
  ...rest
}: {
  id: string;
  label: React.ReactNode;
  description: string;
  status: keyof typeof CategorisedIcon;
  onIconClick?: () => void;
  parentType?: string;
  isInReview?: boolean;
  showCompleted?: boolean;
  showError?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) => {
  const CategorisationStatusIcon = CategorisedIcon[status];
  const isParentTypefinalizeFinancial = parentType === "finalize_financials";

  const toolTipText = {
    SYSTEM_SUGGESTED: "Auto-completed by system; click to confirm",
    MANUAL_ACCEPTED: "Confirmed",
    NOT_ACCEPTED: isParentTypefinalizeFinancial
      ? "Click to mark reviewed"
      : "Click to mark completed",
  };

  const isManuallyAccepted = status === "MANUAL_ACCEPTED";
  const isSystemSuggested = status === "SYSTEM_SUGGESTED";

  let statusLabelForAdmin = "Mark completed";

  if (isSystemSuggested) {
    statusLabelForAdmin = "Auto completed";
  }

  if (isManuallyAccepted || showCompleted) {
    statusLabelForAdmin = "Completed";
  }

  return (
    <button
      {...rest}
      id={id}
      type="button"
      disabled={rest?.disabled}
      className={classNames(
        "all:unset t-flex t-gap-4 t-py-4 sm:t-gap-2 sm:t-py-3 t-w-full t-border-b t-border-0 last:t-border-b-0 t-border-solid t-border-neutral-0 disabled:t-cursor-not-allowed !t-cursor-text t-group t-items-center"
      )}
    >
      <div
        className={classNames("sm:t-w-auto sm:t-flex-1 sm:t-min-w-0", {
          "t-w-[120%]": !isInReview,
          "t-w-10/12": isInReview,
        })}
      >
        <div className="t-text-text-60 t-text-subtext-sm">{label}</div>
      </div>
      <VerticalDivider size="medium" />
      <div className="t-w-1/4 sm:t-w-auto sm:t-shrink-0">
        {rest?.disabled ? (
          <span className="t-text-neutral-20 t-flex t-items-center t-gap-1.5 t-text-button-sm sm:t-text-caption">
            <SolidRoundCheck size="14" />
            <div>{statusLabelForAdmin}</div>
          </span>
        ) : (
          <ConditionalToolTip
            condition={!isManuallyAccepted && toolTipText[status]}
          >
            <div
              onClick={onIconClick}
              className={classNames(
                `t-flex t-items-center t-gap-1.5 t-text-button-sm sm:t-text-caption t-cursor-pointer t-p-2 hover:t-bg-neutral-0 rounded t-w-fit`,
                {
                  "t-text-dark_green": isManuallyAccepted || isSystemSuggested,
                  "t-text-neutral-40": !(
                    isManuallyAccepted || isSystemSuggested
                  ),
                }
              )}
            >
              {showError ? (
                <CircleRed />
              ) : (
                <CategorisationStatusIcon size="14" />
              )}
              <div
                className={classNames({
                  "t-text-dark_green": isManuallyAccepted,
                  "t-text-text-60": !isManuallyAccepted,
                })}
              >
                {statusLabelForAdmin}
              </div>
            </div>
          </ConditionalToolTip>
        )}
      </div>
    </button>
  );
};

export const Checklist = ({
  closingDetails,
  isFormEditable,
  currentStatus,
  otherFinancials,
  isFinancialVersionV1,
}: {
  closingDetails: FinancialClosingDetail;
  isFormEditable: boolean;
  currentStatus: FinancialClosingDetail["status"];
  otherFinancials: FinancialClosing["other_financials"];
  isFinancialVersionV1: boolean;
}) => {
  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const { search } = useLocation();
  const { link } = useConstructInternalLink();
  const { values, setFieldValue, errors } = useFormikContext<{
    checklist_items: ChecklistItem[];
    other_financials: FinancialClosing["other_financials"];
  }>();
  const { checklist_items } = values;
  const { start_date, end_date, uuid: financialClosingId } = closingDetails;

  // Helper function to check if an item has validation errors
  const hasItemError = (itemUuid: string) => {
    if (!errors.checklist_items || !Array.isArray(errors.checklist_items)) {
      return false;
    }

    const itemIndex = checklist_items.findIndex(
      (item) => item.uuid === itemUuid
    );
    return itemIndex >= 0 && Boolean(errors.checklist_items[itemIndex]);
  };

  const reconciliationAccounts = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.RECONCILE_ACCOUNTS
  );

  const verifyCategorisation = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.VERIFY_CATEGORIZATION
  );

  const reviewChanges = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.REVIEW_CHANGES
  );

  const otherActions = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.OTHER_ACTION
  );

  const finalizeFinancials = checklist_items.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS
  );

  const getIndex = (uuid: string) => {
    return checklist_items.findIndex((item) => item.uuid === uuid);
  };

  const getVerifyCategorisationLink = (type: string) => {
    const VERIFY_CATEGORISATION_LINK = {
      [CHECKLIST_ITEM_TYPE.CATEGORIZATION]: {
        uncategorised: true,
      },
      [CHECKLIST_ITEM_TYPE.REVIEW_PENDING_BANK_TRANSFERS]: {},
      [CHECKLIST_ITEM_TYPE.REVIEW_PENDING_CARD_PAYMENTS]: {},
    };

    return link(`/books/transactions`, {
      moreQuery: {
        startDate: start_date,
        endDate: end_date,
        ...VERIFY_CATEGORISATION_LINK[
          type as keyof typeof VERIFY_CATEGORISATION_LINK
        ],
      },
    });
  };

  const [updateFinancialClosingCheckList] =
    useUpdateFinancialClosingCheckListMutation();

  const onCheckListChange = async ({
    resolved_by_admin,
    checklistItemId,
    file_url,
    file_data,
    document_type,
    document_id,
    delete_document,
  }: CheckListUpdatePayload) => {
    try {
      await updateFinancialClosingCheckList({
        entityId: entityId,
        financialClosingId: financialClosingId,
        checklistItemId: checklistItemId,
        payload: {
          admin_resolved: resolved_by_admin,
          file_data: file_data,
          file_url: file_url,
          document_type: document_type,
          delete_document: delete_document,
          document_id: document_id,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const reconciliationAccountsCheckedCount = reconciliationAccounts.filter(
    (item) => {
      const status = getStatus({
        resolved_by_system: item.resolved_by_system,
        resolved_by_admin: item.resolved_by_admin,
      });
      return status === "MANUAL_ACCEPTED" || status === "SYSTEM_SUGGESTED";
    }
  );

  const verifyCategorisationCheckedCount = verifyCategorisation.filter(
    (item) => {
      const status = getStatus({
        resolved_by_system: item.resolved_by_system,
        resolved_by_admin: item.resolved_by_admin,
      });
      return status === "MANUAL_ACCEPTED" || status === "SYSTEM_SUGGESTED";
    }
  );

  const reviewChangesCheckedCount = reviewChanges.filter((item) => {
    const status = getStatus({
      resolved_by_system: item.resolved_by_system,
      resolved_by_admin: item.resolved_by_admin,
    });
    return status === "MANUAL_ACCEPTED" || status === "SYSTEM_SUGGESTED";
  });

  const otherActionsCheckedCount = otherActions.filter((item) => {
    const status = getStatus({
      resolved_by_system: item.resolved_by_system,
      resolved_by_admin: item.resolved_by_admin,
    });

    return status === "MANUAL_ACCEPTED";
  });

  const reviewfinalizeFinancialsCount = finalizeFinancials.filter((item) => {
    const status = getStatus({
      resolved_by_system: item.resolved_by_system,
      resolved_by_admin: item.resolved_by_admin,
    });
    return status === "MANUAL_ACCEPTED" || status === "SYSTEM_SUGGESTED";
  });

  const showCheckedCount =
    currentStatus === CLOSING_STATUS.REVIEW_PENDING ||
    currentStatus === CLOSING_STATUS.COMPLETED;

  const showCompleted =
    currentStatus === CLOSING_STATUS.REVIEW_PENDING ||
    currentStatus === CLOSING_STATUS.COMPLETED;

  const isCompletedState = currentStatus === CLOSING_STATUS.COMPLETED;

  const isOnGoingState =
    currentStatus === CLOSING_STATUS.IN_PROGRESS ||
    currentStatus === CLOSING_STATUS.PENDING_INFO;

  return (
    <>
      {showCheckedCount && (
        <Card.Root
          className={classNames("t-rounded-lg sm:!t-px-3", {
            "t-mb-2": !(showCheckedCount && otherFinancials.length > 0),
          })}
          variant="primary"
        >
          <Card.Body className="">
            <CheckListAccordion.Root
              type="multiple"
              defaultValue={[CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS]}
              className="t-flex t-flex-col t-gap-4"
            >
              <CheckListAccordion.Item
                value={CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS}
              >
                <CheckListAccordion.Trigger>
                  {showCheckedCount ? (
                    <div className="t-flex t-w-full t-justify-between t-items-center t-text-subtext ">
                      <div>Finalize Financials</div>
                      <div className="t-text-text-30">
                        {`${
                          isCompletedState
                            ? finalizeFinancials.length
                            : reviewfinalizeFinancialsCount.length
                        }/${finalizeFinancials.length} reviewed`}
                      </div>
                    </div>
                  ) : (
                    <div className="">Finalize Financials</div>
                  )}
                </CheckListAccordion.Trigger>
                <CheckListAccordion.Content>
                  <div className={"t-flex t-flex-col t-pt-4 t-gap-4 t-w-full"}>
                    <FinalizeFinancials
                      checklistItems={checklist_items}
                      closingDetails={closingDetails}
                      onCheckListChange={onCheckListChange}
                      isFormEditable={isFormEditable}
                      isFinancialVersionV1={isFinancialVersionV1}
                      entityId={entityId}
                    />
                  </div>
                </CheckListAccordion.Content>
              </CheckListAccordion.Item>
            </CheckListAccordion.Root>
          </Card.Body>
        </Card.Root>
      )}
      {showCheckedCount && otherFinancials.length > 0 && !isFormEditable && (
        <div className="t-pt-4 t-pb-2">
          <OtherAttachemetsView otherFinancials={otherFinancials} />
        </div>
      )}

      <section className="t-flex t-flex-col t-gap-4 t-py-2">
        {!isOnGoingState && (
          <div className="t-text-subtitle-lg t-text-text-100 t-pt-2">
            Financial Closing Checklist
          </div>
        )}

        {reconciliationAccounts.length > 0 && (
          <Card.Root className="t-rounded-lg sm:!t-px-3" variant="primary">
            <Card.Body>
              <CheckListAccordion.Root
                type="multiple"
                defaultValue={
                  isOnGoingState
                    ? [CHECKLIST_ITEM_SECTION.RECONCILE_ACCOUNTS]
                    : []
                }
              >
                <CheckListAccordion.Item
                  value={CHECKLIST_ITEM_SECTION.RECONCILE_ACCOUNTS}
                >
                  <CheckListAccordion.Trigger>
                    <div className="t-flex t-justify-between t-w-full t-items-center">
                      <div className="t-flex t-gap-1 t-items-center">
                        <span>Reconcile Accounts</span>
                        <div>
                          <Info
                            text={
                              FINANCIAL_CLOSING_CHECKLIST_TOOLTIP_TEXT.ACCOUNT
                            }
                            align="start"
                          />
                        </div>
                      </div>
                      <div className="t-text-text-30 t-text-subtext sm:t-text-subtext-sm">
                        {`${
                          showCompleted
                            ? reconciliationAccounts.length
                            : reconciliationAccountsCheckedCount.length
                        }/${reconciliationAccounts.length} completed`}
                      </div>
                    </div>
                  </CheckListAccordion.Trigger>
                  <CheckListAccordion.Content>
                    {reconciliationAccounts?.map((item, itemindex) => {
                      const index = getIndex(item.uuid);
                      const status = getStatus({
                        resolved_by_system: item.resolved_by_system,
                        resolved_by_admin: item.resolved_by_admin,
                      }) as keyof typeof CategorisedIcon;

                      const isZero =
                        item.checklist_item_status.split(" ").at(-1) === "$0" ||
                        item.checklist_item_status.split(" ").at(-1) === "$0.0";

                      const verifiedValue = formatNumber(
                        item.checklist_item_status.split("$").at(-1) || "0"
                      );
                      const verifiedValueText = `${
                        item.checklist_item_status
                          .split(" ")
                          .slice(0, -1)
                          .join(" ") || ""
                      } ${`$${verifiedValue}`}`;

                      const showError = hasItemError(item.uuid);

                      return (
                        <div
                          key={item.uuid}
                          className={classNames(
                            "t-w-full t-border-b t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparen",
                            {
                              "t-border-t": itemindex === 0,
                              "t-border-b-0":
                                itemindex === reconciliationAccounts.length - 1,
                            }
                          )}
                        >
                          <ActionItem
                            id={item.uuid}
                            status={status}
                            showError={showError}
                            showCompleted={showCompleted}
                            description={item.checklist_item_status}
                            disabled={!isFormEditable}
                            label={
                              <div className="t-flex t-justify-between t-items-center">
                                <div className="t-flex t-flex-col">
                                  <span className="t-flex t-gap-1 t-text-subtext t-text-text-60 sm:t-text-subtext-sm sm:t-flex-col sm:t-gap-0">
                                    {item?.entity_bank_account?.name || ""}
                                    <span className="t-flex t-gap-1">
                                      <span className="t-text-body-sm t-text-start">
                                        ••••
                                      </span>
                                      {item?.entity_bank_account?.mask || ""}
                                    </span>
                                  </span>
                                  <div
                                    className={classNames("t-text-body-sm", {
                                      "t-text-text-30": isZero,
                                      "t-text-orange-80": !isZero,
                                    })}
                                  >
                                    {verifiedValueText}
                                  </div>
                                </div>
                                <ConditionalLink
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  to={link(
                                    `/books/reconciliation/start/${item.entity_bank_account?.uuid}`
                                  )}
                                  target="_blank"
                                  className="t-text-text-30 hover:t-text-purple"
                                >
                                  <Redirect size={16} />
                                </ConditionalLink>
                              </div>
                            }
                            onIconClick={debounce(() => {
                              onCheckListChange({
                                resolved_by_admin: !item.resolved_by_admin,
                                checklistItemId: item.uuid,
                              });
                              setFieldValue(
                                `checklist_items[${index}].resolved_by_admin`,
                                !item.resolved_by_admin
                              );
                            })}
                          />
                        </div>
                      );
                    })}
                  </CheckListAccordion.Content>
                </CheckListAccordion.Item>
              </CheckListAccordion.Root>
            </Card.Body>
          </Card.Root>
        )}

        {verifyCategorisation.length > 0 && (
          <Card.Root className="t-rounded-lg sm:!t-px-3" variant="primary">
            <Card.Body>
              <CheckListAccordion.Root
                type="multiple"
                defaultValue={
                  isOnGoingState
                    ? [CHECKLIST_ITEM_SECTION.VERIFY_CATEGORIZATION]
                    : []
                }
              >
                <CheckListAccordion.Item
                  value={CHECKLIST_ITEM_SECTION.VERIFY_CATEGORIZATION}
                >
                  <CheckListAccordion.Trigger>
                    <div className="t-flex t-justify-between t-w-full t-items-center">
                      <div className="t-flex t-gap-1 t-items-center">
                        Verify Categorisation
                      </div>
                      <div className="t-text-text-30 t-text-subtext sm:t-text-subtext-sm">
                        {`${
                          showCompleted
                            ? verifyCategorisation.length
                            : verifyCategorisationCheckedCount.length
                        }/${verifyCategorisation.length} completed`}
                      </div>
                    </div>
                  </CheckListAccordion.Trigger>
                  <CheckListAccordion.Content>
                    {verifyCategorisation.map((item, itemindex) => {
                      const index = getIndex(item.uuid);
                      let status = getStatus({
                        resolved_by_system: item.resolved_by_system,
                        resolved_by_admin: item.resolved_by_admin,
                      }) as keyof typeof CategorisedIcon;
                      const isZero =
                        item.checklist_item_status.split(" ").at(-1) === "0";
                      const verifiedValue = formatNumber(
                        item.checklist_item_status.split(" ").at(-1) || "0"
                      );
                      const verifiedValueText = `${
                        item.checklist_item_status
                          .split(" ")
                          .slice(0, -1)
                          .join(" ") || ""
                      } ${verifiedValue}`;
                      const showError = hasItemError(item.uuid);
                      return (
                        <div
                          key={item.uuid}
                          className={classNames(
                            "t-w-full t-border-b t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparen",
                            {
                              "t-border-t": itemindex === 0,
                              "t-border-b-0":
                                itemindex === verifyCategorisation.length - 1,
                            }
                          )}
                        >
                          <ActionItem
                            id={item.uuid}
                            key={item.uuid}
                            status={status}
                            showError={showError}
                            showCompleted={showCompleted}
                            description={item.checklist_item_status}
                            disabled={!isFormEditable}
                            label={
                              <div className="t-flex t-justify-between t-items-center sm:t-justify-start sm:t-gap-2">
                                <div className="t-flex t-flex-col">
                                  <div className="t-flex t-gap-1 t-text-subtext t-text-text-60 sm:t-text-subtext-sm">
                                    {
                                      FINANCIAL_CLOSING_CHECKLIST_LABEL[
                                        item.type
                                      ]
                                    }
                                    <Info
                                      text={
                                        FINANCIAL_CLOSING_CHECKLIST_TOOLTIP_TEXT[
                                          item.type
                                        ]
                                      }
                                      side="right"
                                    />
                                  </div>
                                  <div
                                    className={classNames("t-text-body-sm", {
                                      "t-text-text-30": isZero,
                                      "t-text-orange-80": !isZero,
                                    })}
                                  >
                                    {verifiedValueText}
                                  </div>
                                </div>
                                <ConditionalLink
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  to={getVerifyCategorisationLink(item.type)}
                                  target="_blank"
                                  className="t-text-text-30 hover:t-text-purple"
                                >
                                  <Redirect size={16} />
                                </ConditionalLink>
                              </div>
                            }
                            onIconClick={debounce(() => {
                              onCheckListChange({
                                resolved_by_admin: !item.resolved_by_admin,
                                checklistItemId: item.uuid,
                              });
                              setFieldValue(
                                `checklist_items[${index}].resolved_by_admin`,
                                !item.resolved_by_admin
                              );
                            })}
                          />
                        </div>
                      );
                    })}
                  </CheckListAccordion.Content>
                </CheckListAccordion.Item>
              </CheckListAccordion.Root>
            </Card.Body>
          </Card.Root>
        )}

        {reviewChanges.length > 0 && (
          <Card.Root className="t-rounded-lg sm:!t-px-3" variant="primary">
            <Card.Body>
              <CheckListAccordion.Root
                type="multiple"
                defaultValue={
                  isOnGoingState ? [CHECKLIST_ITEM_SECTION.REVIEW_CHANGES] : []
                }
              >
                <CheckListAccordion.Item
                  value={CHECKLIST_ITEM_SECTION.REVIEW_CHANGES}
                >
                  <CheckListAccordion.Trigger>
                    <div className="t-flex t-justify-between t-w-full t-items-center">
                      <div className="t-flex t-gap-1 t-items-center">
                        Review Changes
                      </div>
                      <div className="t-text-text-30 t-text-subtext sm:t-text-subtext-sm">
                        {`${
                          showCompleted
                            ? reviewChanges.length
                            : reviewChangesCheckedCount.length
                        }/${reviewChanges.length} completed`}
                      </div>
                    </div>
                  </CheckListAccordion.Trigger>
                  <CheckListAccordion.Content>
                    {reviewChanges.map((item, itemindex) => {
                      const index = getIndex(item.uuid);
                      let status = getStatus({
                        resolved_by_system: item.resolved_by_system,
                        resolved_by_admin: item.resolved_by_admin,
                      }) as keyof typeof CategorisedIcon;

                      const isZero =
                        item.checklist_item_status.split(" ").at(-1) === "0";

                      const verifiedValue = formatNumber(
                        item.checklist_item_status.split(" ").at(-1) || "0"
                      );
                      const verifiedValueText = `${
                        item.checklist_item_status
                          .split(" ")
                          .slice(0, -1)
                          .join(" ") || ""
                      } ${verifiedValue}`;

                      const showError = hasItemError(item.uuid);

                      return (
                        <div
                          key={item.uuid}
                          className={classNames(
                            "t-w-full t-border-b t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparen",
                            {
                              "t-border-t": itemindex === 0,
                              "t-border-b-0":
                                itemindex === reviewChanges.length - 1,
                            }
                          )}
                        >
                          <ActionItem
                            id={item.uuid}
                            showCompleted={showCompleted}
                            status={status}
                            showError={showError}
                            description={item.checklist_item_status}
                            disabled={!isFormEditable}
                            label={
                              <div className="t-flex t-justify-between t-items-center">
                                <div className="t-flex t-flex-col">
                                  <div className="t-flex t-gap-1 t-text-subtext t-text-text-60 sm:t-text-subtext-sm">
                                    {
                                      FINANCIAL_CLOSING_CHECKLIST_LABEL[
                                        item.type
                                      ]
                                    }
                                    <Info
                                      text={
                                        FINANCIAL_CLOSING_CHECKLIST_TOOLTIP_TEXT[
                                          item.type
                                        ]
                                      }
                                      side="right"
                                    />
                                  </div>
                                  <div
                                    className={classNames("t-text-body-sm", {
                                      "t-text-text-30": isZero,
                                      "t-text-orange-80": !isZero,
                                    })}
                                  >
                                    {verifiedValueText}
                                  </div>
                                </div>
                                <ConditionalLink
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  to={`${
                                    REVIEW_CHANGES_LINK[
                                      item.type as keyof typeof REVIEW_CHANGES_LINK
                                    ]
                                  }${search}`}
                                  target="_blank"
                                  className="t-text-text-30 hover:t-text-purple"
                                >
                                  <Redirect size={16} />
                                </ConditionalLink>
                              </div>
                            }
                            onIconClick={debounce(() => {
                              onCheckListChange({
                                resolved_by_admin: !item.resolved_by_admin,
                                checklistItemId: item.uuid,
                              });
                              setFieldValue(
                                `checklist_items[${index}].resolved_by_admin`,
                                !item.resolved_by_admin
                              );
                            })}
                          />
                        </div>
                      );
                    })}
                  </CheckListAccordion.Content>
                </CheckListAccordion.Item>
              </CheckListAccordion.Root>
            </Card.Body>
          </Card.Root>
        )}

        {showCheckedCount && (
          <RnDBifurcationSection
            entityId={entityId}
            financialClosingId={financialClosingId}
            isFormEditable={!isCompletedState}
          />
        )}

        {otherActions.length > 0 && (
          <Card.Root className="t-rounded-lg sm:!t-px-3" variant="primary">
            <Card.Body>
              <CheckListAccordion.Root
                type="multiple"
                defaultValue={
                  isOnGoingState ? [CHECKLIST_ITEM_SECTION.REVIEW_CHANGES] : []
                }
              >
                <CheckListAccordion.Item
                  value={CHECKLIST_ITEM_SECTION.REVIEW_CHANGES}
                >
                  <CheckListAccordion.Trigger>
                    <div className="t-flex t-justify-between t-w-full t-items-center">
                      <div className="t-flex t-gap-1 t-items-center">
                        Other Actions
                      </div>
                      <div className="t-text-text-30 t-text-subtext sm:t-text-subtext-sm">
                        {`${
                          showCompleted
                            ? otherActions.length
                            : otherActionsCheckedCount.length
                        }/${otherActions.length} completed`}
                      </div>
                    </div>
                  </CheckListAccordion.Trigger>
                  <CheckListAccordion.Content>
                    {otherActions.map((item, itemindex) => {
                      const index = getIndex(item.uuid);
                      let status = getStatus({
                        resolved_by_system: item.resolved_by_system,
                        resolved_by_admin: item.resolved_by_admin,
                      }) as keyof typeof CategorisedIcon;

                      const showError = hasItemError(item.uuid);

                      return (
                        <div
                          key={item.uuid}
                          className={classNames(
                            "t-w-full t-border-b t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparen",
                            {
                              "t-border-t": itemindex === 0,
                              "t-border-b-0":
                                itemindex === otherActions.length - 1,
                            }
                          )}
                        >
                          <ActionItem
                            id={item.uuid}
                            showCompleted={showCompleted}
                            status={status}
                            showError={showError}
                            description={item.checklist_item_status}
                            disabled={!isFormEditable}
                            label={
                              <div className="t-flex t-gap-1 t-text-subtext t-text-text-60 t-flex-col">
                                <span className="t-text-subtext">
                                  {item.name}
                                </span>
                                <span className="t-text-body-sm">
                                  {item.description}
                                </span>
                              </div>
                            }
                            onIconClick={debounce(() => {
                              onCheckListChange({
                                resolved_by_admin: !item.resolved_by_admin,
                                checklistItemId: item.uuid,
                              });
                              setFieldValue(
                                `checklist_items[${index}].resolved_by_admin`,
                                !item.resolved_by_admin
                              );
                            })}
                          />
                        </div>
                      );
                    })}
                  </CheckListAccordion.Content>
                </CheckListAccordion.Item>
              </CheckListAccordion.Root>
            </Card.Body>
          </Card.Root>
        )}
      </section>

      {!showCheckedCount && (
        <RnDBifurcationSection
          entityId={entityId}
          financialClosingId={financialClosingId}
          isFormEditable={!isCompletedState}
        />
      )}

      {!showCheckedCount && (
        <Card.Root className="t-rounded-lg t-mt-2 sm:!t-px-3" variant="primary">
          <Card.Body>
            <CheckListAccordion.Root
              type="multiple"
              defaultValue={[CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS]}
              className="t-flex t-flex-col t-gap-4"
            >
              <CheckListAccordion.Item
                value={CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS}
              >
                <CheckListAccordion.Trigger>
                  {showCheckedCount ? (
                    <div className="t-flex t-w-full t-justify-between t-items-center t-text-subtext t-text-text-30">
                      <div>Finalize Financials</div>
                      <div>
                        {`${reviewfinalizeFinancialsCount.length}/${finalizeFinancials.length} reviewed`}
                      </div>
                    </div>
                  ) : (
                    <div>Finalize Financials</div>
                  )}
                </CheckListAccordion.Trigger>
                <CheckListAccordion.Content>
                  <FinalizeFinancials
                    checklistItems={checklist_items}
                    closingDetails={closingDetails}
                    onCheckListChange={onCheckListChange}
                    isFormEditable={isFormEditable}
                    isFinancialVersionV1={isFinancialVersionV1}
                    entityId={entityId}
                  />
                </CheckListAccordion.Content>
              </CheckListAccordion.Item>
            </CheckListAccordion.Root>
          </Card.Body>
        </Card.Root>
      )}
    </>
  );
};
